<script setup lang="ts">
import type { RouteLocationRaw } from 'vue-router';
import { computed, ref } from 'vue';
import {
  useRoute,
  useRouter,
} from 'vue-router';

import type { IDepartmentItem } from '@modules/affiliates-approved/types';
import type { IAcceptAffiliateForm, IAffiliatesApprovePayload } from '@modules/affiliates-pending/types';
import type { IApiFormErrors } from '@modules/core/services/query/types';
import type { FormFlattenKey, FormFlattenValue } from '@modules/forms/components/VForm/types';
import VBackButton from '@components/buttons/VBackButton/VBackButton.vue';
import VCard from '@components/card/VCard/VCard.vue';
import { useAffiliateManagersQuery, useAffiliatesDepartmentListQuery } from '@modules/affiliates-approved/queries';
import { useAffiliateChangeStatus } from '@modules/affiliates-pending/composables';
import {
  AFFILIATES_PENDING_LIST_ROUTE_NAME,
  TRANSLATION_GROUP_AFFILIATES_PENDING,
} from '@modules/affiliates-pending/constants';
import { AFFILIATES_REQUEST_HISTORY_LIST_ROUTE_NAME, AFFILIATES_REQUEST_HISTORY_REAPPROVE_ROUTE_NAME } from '@modules/affiliates-request-history/constants';
import { EAasmState } from '@modules/core/enums';
import VForm from '@modules/forms/components/VForm/VForm.vue';
import { EFormInputTypes } from '@modules/forms/enums';
import { useI18n } from '@modules/i18n/composables';
import { useNamespaceRoutes } from '@modules/permissions/composables';
import { isString } from '@utils/guards';

const showDepartmentWarning = ref(false);

const { getTranslation } = useI18n();
const router = useRouter();
const route = useRoute();
const { isCurrentRouteChildOf } = useNamespaceRoutes();
const {
  selectManagerPermission,
  selectDepartmentPermission,
  isApproveLoading,
  isReapproveLoading,
  approveErrors,
  reapproveErrors,
  clearApproveError,
  clearReapproveError,
  confirmApprove,
  confirmReapprove,
} = useAffiliateChangeStatus();

const id = computed<number>(() => (isString(route.params.id) ? Number(route.params.id) : 0));

const { data: managers, isLoading: isManagersLoading } = useAffiliateManagersQuery(selectManagerPermission);
const { data: departments, isLoading: isDepartmentsLoading } = useAffiliatesDepartmentListQuery(selectDepartmentPermission);

const isReapprovePage = computed<boolean>(() => isCurrentRouteChildOf({ name: AFFILIATES_REQUEST_HISTORY_REAPPROVE_ROUTE_NAME }));
const isSaveLoading = computed<boolean>(() => isReapprovePage.value ? isReapproveLoading.value : isApproveLoading.value);
const errors = computed<IApiFormErrors | undefined>(() => isReapprovePage.value ? reapproveErrors.value : approveErrors.value);
const backRoute = computed<RouteLocationRaw>(() => ({ name: isReapprovePage.value ? AFFILIATES_REQUEST_HISTORY_LIST_ROUTE_NAME : AFFILIATES_PENDING_LIST_ROUTE_NAME }));

const departmentList = computed<IDepartmentItem[] | undefined>(() => departments.value?.filter(({ aasm_state }) => aasm_state === EAasmState.STANDARD));

const departmentWarning = computed<string | undefined>(() =>
  showDepartmentWarning.value
    ? getTranslation(TRANSLATION_GROUP_AFFILIATES_PENDING, 'ACCEPT_SAVE_DEPARTMENT_WARNING').value
    : undefined,
);

const formSchema = computed<IAcceptAffiliateForm>(() => {
  const schema: IAcceptAffiliateForm = {};

  if (selectManagerPermission.value) {
    schema.manager_id = {
      type: EFormInputTypes.SELECT,
      options: managers.value ?? [],
      label: getTranslation(TRANSLATION_GROUP_AFFILIATES_PENDING, 'ACCEPT_CHOOSE_MANAGER').value,
      optionValue: 'id',
      optionLabel: 'name',
      required: true,
      loading: isManagersLoading.value,
    };
  }

  if (selectDepartmentPermission.value) {
    schema.department_id = {
      type: EFormInputTypes.SELECT,
      options: departmentList.value ?? [],
      label: getTranslation(TRANSLATION_GROUP_AFFILIATES_PENDING, 'ACCEPT_CHOOSE_DEPARTMENT').value,
      optionValue: 'id',
      optionLabel: 'name',
      required: true,
      description: departmentWarning.value,
      descriptionSeverity: 'warn',
      loading: isDepartmentsLoading.value,
    };
  }

  return schema;
});

function onChange<
  T extends IAffiliatesApprovePayload,
  K extends FormFlattenKey<T>,
>(
  key: K,
  value: FormFlattenValue<T, K> | undefined,
): void {
  if (key === 'department_id' && !!value) {
    showDepartmentWarning.value = true;
  }
}

async function onSubmit(values: IAffiliatesApprovePayload): Promise<void> {
  const saveApprove = isReapprovePage.value ? confirmReapprove : confirmApprove;

  saveApprove(id.value, values, undefined, saveCallback);
}

function saveCallback(): void {
  void router.push(backRoute.value);
}

function clearError(key: string): void {
  if (isReapprovePage.value) {
    clearReapproveError(key);
  }
  clearApproveError(key);
}
</script>

<template>
  <VCard v-data-qa-auto-id="'AffiliatesPendingApprovePage'"
    type="form"
    :title="$getTranslation(TRANSLATION_GROUP_AFFILIATES_PENDING, 'ACCEPT_HEADER')"
  >
    <VForm
      :schema="formSchema"
      :submit-btn-label="$getTranslation(TRANSLATION_GROUP_AFFILIATES_PENDING, 'ACCEPT_ACCEPT_BTN')"
      :is-loading="isSaveLoading"
      :custom-errors="errors"
      @interacted="clearError"
      @change="onChange"
      @submit="onSubmit"
    >
      <template #secondaryBtn>
        <VBackButton
          :to="backRoute"
          :label="$getTranslation(TRANSLATION_GROUP_AFFILIATES_PENDING, 'ACCEPT_BACK_BTN')"
        />
      </template>
    </VForm>
  </VCard>
</template>
